import { Theme } from '@mui/material'

import { GraphDatapoint, GraphSeriesConfig, GraphSeriesEvents } from '../types/graphSeriesConfig'
import { Datapoint } from '../types/datapoint'
import { LogEvent } from '../types/logEvent'
import { EventType } from '../types/enums/eventType'
import {
  BasicDataParam,
  CalculatedDataParam,
  GraphDataParam,
  GraphDataParamString,
  GraphDataParamStringProfile,
  SensorconfigParam,
} from '../types/enums/graphDataParam'
import { hasBetaFeature } from './localStorageUtils'
import { Log } from '../types/log'
import { Sensorconfig } from '../types/sensorconfig'
import { getLogName } from './logUtils'
import { Machine } from '../types/machine'
import { User } from '../types/user'
import { Profile } from '../types/profile'
import { generatePrecisePlotPoints } from '../utils/precisePlot'
import { isFahrenheit, isPounds } from './localStorageUtils'
import { SeriesType } from '../types/enums/seriesType'
import { SensorType } from '../types/enums/sensorType'
import { getProfileSeries } from './profileEditorUtils'

export enum AxisConfig {
  X_AXIS_TOP = 'X_AXIS_TOP',
  X_AXIS_BOTTOM = 'X_AXIS_BOTTOM',
  Y_AXIS_TEMPERATURE = 'Y_AXIS_TEMPERATURE',
  Y_AXIS_PERCENT = 'Y_AXIS_PERCENT',
  Y_AXIS_PRESSURE = 'Y_AXIS_PRESSURE',
  Y_AXIS_ROR = 'Y_AXIS_ROR',
  Y_AXIS_CRACKS = 'Y_AXIS_CRACKS',
  Y_AXIS_TOTAL_CRACKS = 'Y_AXIS_TOTAL_CRACKS',
}

const ROR_INTERVAL = 30
const PREDICTION_CALC_AVERAGE = 15 // calculate predicted tangent from current - # temperature values

export const hexToRGBA = (hexColor: string, alpha: number): string => {
  var r = parseInt(hexColor.slice(1, 3), 16),
    g = parseInt(hexColor.slice(3, 5), 16),
    b = parseInt(hexColor.slice(5, 7), 16)

  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

export const indexToColor = (index?: number): 'lighter' | 'light' | 'main' | 'dark' | 'darker' => {
  if (index === 0) {
    return 'main'
  } else if (index === 1) {
    return 'dark'
  } else if (index === 2) {
    return 'light'
  } else if (index === 3) {
    return 'darker'
  } else if (index === 4) {
    return 'lighter'
  }
  return 'main'
}

export const getSeriesColor = (param: GraphDataParam, theme: Theme, index?: number): string => {
  if (param === BasicDataParam.BT) {
    return theme.palette.bt[indexToColor(index)] || theme.palette.bt.main
  } else if (param === BasicDataParam.ET) {
    return theme.palette.et[indexToColor(index)] || theme.palette.et.main
  } else if (param === BasicDataParam.POWER) {
    return theme.palette.power[indexToColor(index)] || theme.palette.power.main
  } else if (param === BasicDataParam.INLET) {
    return theme.palette.inletTemp[indexToColor(index)] || theme.palette.inletTemp.main
  } else if (param === BasicDataParam.DRUM) {
    return theme.palette.drumTemp[indexToColor(index)] || theme.palette.drumTemp.main
  } else if (param === BasicDataParam.FAN) {
    return theme.palette.fan[indexToColor(index)] || theme.palette.fan.main
  } else if (param === BasicDataParam.RPM) {
    return theme.palette.rpm[indexToColor(index)] || theme.palette.rpm.main
  } else if (param === BasicDataParam.DRUM_PRESSURE) {
    return theme.palette.drumPressure[indexToColor(index)] || theme.palette.drumPressure.main
  } else if (param === CalculatedDataParam.ROR) {
    return theme.palette.ror[indexToColor(index)] || theme.palette.ror.main
  } else if (param === BasicDataParam.CRACK || param === CalculatedDataParam.TOTAL_CRACKS) {
    return theme.palette.cracks[indexToColor(index)] || theme.palette.cracks.main
  } else if (param === BasicDataParam.TARGET) {
    return theme.palette.target.main
  }
  return ''
}

export const getGraphLimits = (param: GraphDataParam): { min: number; max: number } => {
  let minValue = 0
  let maxValue = 100

  // use string as key to trick typescript (does not like enum as key here)
  // RoR's minimum graph limit is changed in logChart.tsx
  const GraphLimits = {
    [`${BasicDataParam.BT}`]: { min: 0, max: 250, isTemp: true },
    [`${BasicDataParam.ET}`]: { min: 0, max: 320, isTemp: true },
    [`${BasicDataParam.FAN}`]: { min: 0, max: 100, isTemp: false },
    [`${BasicDataParam.RPM}`]: { min: 20, max: 70, isTemp: false },
    [`${BasicDataParam.POWER}`]: { min: 0, max: 100, isTemp: false },
    [`${BasicDataParam.INLET}`]: { min: 0, max: 500, isTemp: true },
    [`${BasicDataParam.DRUM_PRESSURE}`]: { min: 0, max: 3500, isTemp: false },
    [`${CalculatedDataParam.ROR}`]: { min: 0, max: 45, isTemp: true },
  }

  if (GraphLimits.hasOwnProperty(param)) {
    const limits = GraphLimits[param]
    minValue = limits.isTemp ? convertToTempScale(limits.min, 0, param) : limits.min
    maxValue = limits.isTemp ? convertToTempScale(limits.max, 0, param) : limits.max
  }
  return { min: minValue, max: maxValue }
}

export const convertToTempScale = (tempValue: number, decimals: number, paramName?: GraphDataParam): number => {
  const isFahrenheitEnabled = isFahrenheit()
  const addConstant = paramName === CalculatedDataParam.ROR ? 0 : 32
  const convertedValue = isFahrenheitEnabled ? tempValue * 1.8 + addConstant : tempValue
  const roundBy = Math.pow(10, decimals)
  return Math.round(convertedValue * roundBy) / roundBy
}

export const getDisplayTemperature = (tempValue: number | null, decimals?: number): string => {
  return tempValue
    ? `${convertToTempScale(tempValue, 2).toFixed(decimals !== undefined ? decimals : 2)}${getValueSuffix(
        SeriesType.TEMPERATURE
      )}`
    : ''
}

export const convertFromTempScale = (tempValue: number): number => {
  const isFahrenheitEnabled = isFahrenheit()
  return isFahrenheitEnabled ? (tempValue - 32) * (5 / 9) : tempValue
}

export const getValueSuffix = (seriesType: SeriesType): string => {
  const isFahrenheitEnabled = isFahrenheit()
  if (seriesType === SeriesType.ROR) {
    return isFahrenheitEnabled ? '°F/min' : '°C/min'
  } else if (seriesType === SeriesType.TEMPERATURE) {
    return isFahrenheitEnabled ? '°F' : '°C'
  } else if (seriesType === SeriesType.RPM) {
    return 'RPM'
  } else if (seriesType === SeriesType.PERCENT) {
    return '%'
  } else if (seriesType === SeriesType.PRESSURE) {
    return 'Pa'
  } else if (seriesType === SeriesType.WEIGHT) {
    return isPounds() ? 'oz' : 'g'
  }
  return ''
}

const getTotalCracks = (datapoints: Array<Datapoint>): number => {
  return datapoints.reduce((acc, curr) => acc + (curr.crack || 0), 0)
}

export const getXaxis = (seriesType: SeriesType): string => {
  return seriesType === SeriesType.PERCENT || seriesType === SeriesType.RPM || seriesType === SeriesType.PRESSURE
    ? AxisConfig.X_AXIS_BOTTOM
    : AxisConfig.X_AXIS_TOP
}

export const getYaxis = (seriesType: SeriesType): string => {
  if (seriesType === SeriesType.PERCENT || seriesType === SeriesType.RPM) {
    return AxisConfig.Y_AXIS_PERCENT
  } else if (seriesType === SeriesType.PRESSURE) {
    return AxisConfig.Y_AXIS_PRESSURE
  } else if (seriesType === SeriesType.ROR) {
    return AxisConfig.Y_AXIS_ROR
  } else if (seriesType === SeriesType.CRACK) {
    return AxisConfig.Y_AXIS_CRACKS
  } else if (seriesType === SeriesType.TOTAL_CRACKS) {
    return AxisConfig.Y_AXIS_TOTAL_CRACKS
  }
  return AxisConfig.Y_AXIS_TEMPERATURE
}

const sensorTypeToSeriesType = (sensorType: SensorType): SeriesType => {
  return sensorType === SensorType.Pressure ? SeriesType.PRESSURE : SeriesType.TEMPERATURE
}

const getCommonSeriesConfig = (param: GraphDataParam, seriesType: SeriesType, theme: Theme): GraphSeriesConfig => {
  return {
    param: param,
    seriesId: param,
    seriesLabel: GraphDataParamString[param],
    color: getSeriesColor(param, theme),
    seriesType: seriesType,
    datapoints: [],
  }
}

const getCumulativeValues = (data: Array<GraphDatapoint>): Array<GraphDatapoint> => {
  return data.reduce((previous: Array<GraphDatapoint>, currentValue: GraphDatapoint) => {
    return [
      ...previous,
      {
        msec: currentValue.msec,
        dataValue: (previous[previous.length - 1]?.dataValue || 0) + (currentValue.dataValue || 0),
      },
    ]
  }, [])
}

export const getPlotSeriesFromLog = (
  logDatapoints: Array<Datapoint>,
  sensorconfig: Sensorconfig | null,
  theme: Theme,
  user: User | null
): Array<GraphSeriesConfig> => {
  const returnArray: Array<GraphSeriesConfig> = [
    {
      ...getCommonSeriesConfig(BasicDataParam.FAN, SeriesType.PERCENT, theme),
      datapoints: getSeriesDatapoints(BasicDataParam.FAN, logDatapoints, user),
    },
    {
      ...getCommonSeriesConfig(BasicDataParam.RPM, SeriesType.RPM, theme),
      datapoints: getSeriesDatapoints(BasicDataParam.RPM, logDatapoints, user),
    },
    {
      ...getCommonSeriesConfig(BasicDataParam.POWER, SeriesType.PERCENT, theme),
      datapoints: getSeriesDatapoints(BasicDataParam.POWER, logDatapoints, user),
    },
    {
      ...getCommonSeriesConfig(BasicDataParam.BT, SeriesType.TEMPERATURE, theme),
      datapoints: getSeriesDatapoints(BasicDataParam.BT, logDatapoints, user),
    },
    {
      ...getCommonSeriesConfig(BasicDataParam.ET, SeriesType.TEMPERATURE, theme),
      datapoints: getSeriesDatapoints(BasicDataParam.ET, logDatapoints, user),
    },
    {
      ...getCommonSeriesConfig(CalculatedDataParam.ROR, SeriesType.ROR, theme),
      datapoints: getSeriesDatapoints(CalculatedDataParam.ROR, logDatapoints, user),
    },
    {
      ...getCommonSeriesConfig(BasicDataParam.CRACK, SeriesType.CRACK, theme),
      datapoints: getSeriesDatapoints(BasicDataParam.CRACK, logDatapoints, user),
    },
    {
      ...getCommonSeriesConfig(CalculatedDataParam.TOTAL_CRACKS, SeriesType.TOTAL_CRACKS, theme),
      seriesLabel: `Total cracks (${getTotalCracks(logDatapoints)})`,
      datapoints: getSeriesDatapoints(CalculatedDataParam.TOTAL_CRACKS, logDatapoints, user),
    },
  ]

  // if all target values are 100 or below, we expect this to be a power profile target series
  const isPowerTargetSeries = logDatapoints.every((x) => (x.target || 0) <= 100)
  returnArray.push({
    ...getCommonSeriesConfig(
      BasicDataParam.TARGET,
      isPowerTargetSeries ? SeriesType.PERCENT : SeriesType.TEMPERATURE,
      theme
    ),
    datapoints: getSeriesDatapoints(BasicDataParam.TARGET, logDatapoints, user),
  })

  if (hasBetaFeature('pcb_temp')) {
    returnArray.push({
      ...getCommonSeriesConfig(BasicDataParam.PCB_TEMP, SeriesType.TEMPERATURE, theme),
      datapoints: getSeriesDatapoints(BasicDataParam.PCB_TEMP, logDatapoints, user),
    })
  }
  if (sensorconfig) {
    if (sensorconfig.has_drum) {
      returnArray.push({
        ...getCommonSeriesConfig(BasicDataParam.DRUM, SeriesType.TEMPERATURE, theme),
        datapoints: getSeriesDatapoints(BasicDataParam.DRUM, logDatapoints, user),
      })
    }
    if (sensorconfig.has_inlet) {
      returnArray.push({
        ...getCommonSeriesConfig(BasicDataParam.INLET, SeriesType.TEMPERATURE, theme),
        datapoints: getSeriesDatapoints(BasicDataParam.INLET, logDatapoints, user),
      })
    }
    if (sensorconfig.has_pressure) {
      returnArray.push({
        ...getCommonSeriesConfig(BasicDataParam.DRUM_PRESSURE, SeriesType.PRESSURE, theme),
        datapoints: getSeriesDatapoints(BasicDataParam.DRUM_PRESSURE, logDatapoints, user),
      })
    }
    if (hasBetaFeature('et_ror')) {
      returnArray.push({
        ...getCommonSeriesConfig(CalculatedDataParam.ET_ROR, SeriesType.ROR, theme),
        datapoints: getSeriesDatapoints(CalculatedDataParam.ET_ROR, logDatapoints, user),
      })
    }
    if (hasBetaFeature('ext_ror') && sensorconfig.enabled_sensors.some((e) => e.sensor_type === 3)) {
      returnArray.push({
        ...getCommonSeriesConfig(CalculatedDataParam.EXT_ROR, SeriesType.ROR, theme),
        datapoints: getSeriesDatapoints(CalculatedDataParam.EXT_ROR, logDatapoints, user),
      })
    }
    // add series from sensorconfig
    const sortedSeriesConfig = sensorconfig.enabled_sensors
      .filter((x) => x.sensor_type !== SensorType.InletTemp && x.sensor_type !== SensorType.DrumTemp)
      .sort((a, b) => (a.order || 0) - (b.order || 0))
    sortedSeriesConfig.forEach((sensorconfigseries) => {
      returnArray.push({
        param: sensorconfigseries.name,
        seriesId: sensorconfigseries.name,
        seriesLabel: sensorconfigseries.label,
        color: sensorconfigseries.color,
        seriesType: sensorTypeToSeriesType(sensorconfigseries.sensor_type),
        datapoints: getSeriesDatapoints(sensorconfigseries.name, logDatapoints, user),
      })
    })
  }

  return returnArray
}

const linearInterpolateDatapointValues = (values: Array<GraphDatapoint>): Array<GraphDatapoint> => {
  const interpolatedValues: Array<GraphDatapoint> = values.length > 0 ? [values[0]] : []
  for (let i = 1; i < values.length; i++) {
    const from = values[i - 1]
    const to = values[i]
    const gapLength = (to.msec - from.msec + 1000) / 1000
    if (gapLength > 0) {
      const increase = ((to.dataValue || 0) - (from.dataValue || 0)) / (gapLength - 1)
      for (let j = 1; j < gapLength; j++) {
        const valueToAdd = {
          msec: from.msec + j * 1000,
          dataValue: (from.dataValue || 0) + increase * j,
        }
        interpolatedValues.push(valueToAdd)
      }
    } else {
      interpolatedValues.push(values[i])
    }
  }

  return interpolatedValues
}

export const getRorInterval = (user: User | null): number => {
  return user?.ror_interval || ROR_INTERVAL
}

export const generateRor = (tempValues: Array<GraphDatapoint>, user: User | null): Array<GraphDatapoint> => {
  const rorInterval = getRorInterval(user)
  const rorPoints: Array<GraphDatapoint> = []

  // linear interpolate missing bt values
  const interpolatedValues = linearInterpolateDatapointValues(tempValues)

  for (let i = rorInterval; i < interpolatedValues.length; i++) {
    const numValue = (interpolatedValues[i].dataValue || 0) - (interpolatedValues[i - rorInterval].dataValue || 0)
    rorPoints.push({
      msec: interpolatedValues[i].msec,
      dataValue: Math.max(numValue) * (60 / rorInterval),
    })
  }
  return rorPoints
}

export const datapointsToSeries = (datapoints: Array<Datapoint>, logParam: GraphDataParam): Array<GraphDatapoint> => {
  return datapoints.map((x) => {
    return { msec: x.msec, dataValue: x[logParam as SensorconfigParam | BasicDataParam] }
  })
}

const getSeriesDatapoints = (
  param: GraphDataParam,
  datapoints: Array<Datapoint>,
  user: User | null
): Array<GraphDatapoint> => {
  if (param === CalculatedDataParam.ROR) {
    return generateRor(datapointsToSeries(datapoints, BasicDataParam.BT), user)
  } else if (param === CalculatedDataParam.ET_ROR) {
    return generateRor(datapointsToSeries(datapoints, BasicDataParam.ET), user)
  } else if (param === CalculatedDataParam.EXT_ROR) {
    return generateRor(datapointsToSeries(datapoints, SensorconfigParam.RTD3), user)
  } else if (param === CalculatedDataParam.TOTAL_CRACKS) {
    return getCumulativeValues(datapointsToSeries(datapoints, BasicDataParam.CRACK))
  } else {
    return datapointsToSeries(datapoints, param)
  }
}

export const getCompareSeriesConfig = (
  logParam: GraphDataParam,
  logData: Log,
  seriesLabel: string,
  seriesColor: string,
  seriesType: SeriesType,
  datapoints: Array<Datapoint>,
  user: User | null
): GraphSeriesConfig => {
  return {
    param: logParam,
    seriesId: `${logData.id}-${logParam}`,
    seriesLabel: seriesLabel,
    color: seriesColor,
    seriesType: seriesType,
    datapoints: getSeriesDatapoints(logParam, datapoints, user),
  }
}

const getTpDatapoint = (btPoints: Array<GraphDatapoint>): GraphDatapoint | null => {
  if (btPoints.length <= 10) {
    return null
  }
  let prevValue = 1000
  for (let i = 10; i < btPoints.length; i++) {
    if ((btPoints[i].dataValue || 0) > prevValue) {
      return btPoints[i - 1]
    }
    prevValue = btPoints[i].dataValue || 1000
  }
  return null
}

export const hasTurningPoint = (rorPoints: Array<GraphDatapoint>): boolean => {
  return rorPoints.some((x) => (x.dataValue || 0) > 0)
}

export const getMaxChartDatapoints = (series: Array<GraphSeriesConfig>): number => {
  const maxTime = Math.max(getMaxDatapointMsec(series.map((x) => x.datapoints)), 240000)
  return maxTime + (60000 - (maxTime % 60000))
}

export const calculatePredictedTemperatures = (
  plotSeries: Array<GraphSeriesConfig>,
  hasTp: boolean,
  theme: Theme
): Array<GraphSeriesConfig> => {
  const expectedChartLength = getMaxChartDatapoints(plotSeries) / 1000

  return plotSeries.map((x) => {
    const predictionSeriesLength = x.datapoints.length || 0
    let datapoints: Array<GraphDatapoint> = []

    if (hasTp && x.datapoints.length > PREDICTION_CALC_AVERAGE) {
      const lastSec = x.datapoints[predictionSeriesLength - 1].msec / 1000
      const lastValue = x.datapoints[predictionSeriesLength - 1].dataValue || 0
      const calculationValue = x.datapoints[predictionSeriesLength - PREDICTION_CALC_AVERAGE].dataValue || 0
      const valueDiff = (lastValue - calculationValue) / (PREDICTION_CALC_AVERAGE - 1)
      const closeToEndExtra = expectedChartLength - lastSec < 120 ? 120 : 0 // add an extra 120 seconds if there is less than 120 seconds left

      let prevValue = lastValue
      datapoints = Array.from(Array(expectedChartLength + closeToEndExtra - lastSec).keys()).map((_x, index) => {
        prevValue = prevValue + valueDiff
        return {
          msec: (index + lastSec) * 1000,
          dataValue: prevValue,
        }
      })
    }
    return {
      ...x,
      seriesId: `predicted-target-${x.param}`,
      seriesLabel: x.param === BasicDataParam.BT ? 'liveview.predictedbt' : 'liveview.predictedet',
      color: x.param === BasicDataParam.BT ? theme.palette.bt.dark : theme.palette.et.dark,
      datapoints: datapoints,
    }
  })
}

export const getLogEvents = (
  events: Array<LogEvent>,
  logData: Log | null,
  batchString: string,
  btPoints: Array<GraphDatapoint>,
  logId?: number
): GraphSeriesEvents => {
  const logEvents = events
    .filter((x) => x.type !== EventType.Charge && x.type !== EventType.UNUSED)
    .map((x) => {
      return {
        ...x,
        bt: btPoints.find((y) => y.msec === x.msec)?.dataValue || null,
      }
    })
  const tpDatapoint = getTpDatapoint(btPoints)
  if (tpDatapoint) {
    logEvents.push({
      msec: tpDatapoint.msec,
      type: EventType.TurningPoint,
      bt: tpDatapoint.dataValue,
    })
  }
  return {
    labelPrefix: logData ? getLogName(logData, '', batchString) : '',
    logId: logId,
    events: logEvents,
  }
}

export const getChartMaxTemp = (user: User | undefined | null, machine: Machine | undefined | null): number => {
  if (machine?.is_p2000) {
    return user?.p2000_chart_max_temp || 550
  }
  return user?.s100_chart_max_temp || 325
}

export const getFullscreenElement = () => {
  // @ts-ignore: browser-specific properties
  return document.fullscreenElement || document.webkitFullscreenElement
}

export const isFullscreenEnabled = () => {
  // @ts-ignore: browser-specific properties
  return document.fullscreenEnabled || document.webkitFullscreenEnabled
}

export const getExpectedProfileSeries = (theme: Theme, profile: Profile | null): Array<GraphSeriesConfig> => {
  if (!profile) {
    return []
  }
  return getProfileSeries(profile).map((x) => {
    return {
      param: x.param,
      seriesId: `expected-target-${x.param}`,
      seriesLabel: GraphDataParamStringProfile[x.param],
      color: getSeriesColor(x.param, theme),
      seriesType: x.seriesType,
      datapoints: generatePrecisePlotPoints(x.data),
    }
  })
}

// calculate the max msec of several series
export const getMaxDatapointMsec = (seriesDatapoints: Array<Array<GraphDatapoint>>): number => {
  return seriesDatapoints.reduce((max: number, current: Array<GraphDatapoint>) => {
    const lastDatapointMsec = current.length > 0 ? current[current.length - 1].msec : 0
    return Math.max(lastDatapointMsec, max)
  }, 0)
}

export const getPlotLineConfig = (
  xValue: number,
  color: string,
  text: string,
  id: string,
  theme: Theme,
  opacity: number
): Array<Highcharts.XAxisPlotLinesOptions> => {
  // add a plotline to be the fake label background-color, since useHTML for plotline label results in overlapping tooltips
  return [
    {
      value: xValue,
      width: 2,
      color: color,
      id: `plotLines-${id}`,
      zIndex: 5,
      ...(!!text && {
        label: {
          x: -4,
          y: 6,
          style: {
            letterSpacing: '-2.5px',
            opacity: 0.8,
            color: theme.palette.background.default, // use default colors from MUI (easier than separate config for plotlines)
          },
          text: '|'.repeat(text.length * 15),
        },
      }),
    },
    {
      value: xValue,
      color: 'transparent',
      width: 2,
      id: `plotLines-bg-${id}`,
      zIndex: 5,
      ...(!!text && {
        label: {
          x: -4,
          style: {
            opacity: opacity,
            color: theme.palette.text.secondary, // use default colors from MUI (easier than separate config for plotlines)
          },
          text: text,
        },
      }),
    },
  ]
}
